/**
 * 67.75, supplies as containers (DECISIONS 224 (5), the owner: "for supplies I think we just decide how much of the supplies
 * that are currently around the map would fit into a container and just do the basics to mock out what fits where and make
 * that part of the default starting pool, … we probably also will want to introduce some smaller utility drone that can
 * deliver supplies into the field, but is only big enough to carry one of those smaller supply loads."). Data only: nothing
 * in the sim reads it yet (the motor pool draws its containers packed by it).
 *
 * Three sizes of load, in today's units (each derived from the constant it stands for):
 *   - a **drop crate** (the utility drone's one load): a point crate's refill, SUPPLY.point;
 *   - a **pallet** (1.2 × 0.8 m, the supply dump's pallet; a supply truck's bed takes four): a supply dump's stock, SUPPLY —
 *     two drop crates' worth; one truck load today (CONVOY.load and its material) is three pallets, two of stores, one of material;
 *   - a **container** (the frame family's walled equipment container): 32 pallet spots, two across, eight along, two high in
 *     its 7.2 × 3.1 × 3.5 m clear (a spot is a pallet's footprint; a FOB's kit takes four).
 */
import type { ContainerPacking, PalletKind, SupplyContents } from '../types';
import { SUPPLY, CONVOY, GUN_STOCK, BUILD_KITS, DRONE_PAYLOADS } from './action';

/** The pallet (metres): the supply dump's (rendering/footholdLooks.ts pallet), its load stacked to `height` with the pallet. */
export const PALLET = { length: 1.2, width: 0.8, height: 1.35 };
/** The drop crate (metres): what the utility drone carries, slung under it. */
export const DROP_CRATE = { length: 0.8, width: 0.6, height: 0.5, contents: { bandages: SUPPLY.point.bandages, magazines: SUPPLY.point.magazines } as SupplyContents };
/**
 * 69.32 (the drone_drop sheet's numbers; DECISIONS 270 (5): "quick hot drop landing"): the supply drone's run — it flies at
 * `speedPeople` a second `heightPeople` up, climbing and coming down over `climbSeconds`, stands `groundSeconds` on the
 * ground (down, crate off, up), and is reloaded `reloadSeconds` after it is back; a side holds `stock` by default (268 (5):
 * a count, like its vehicles'). Step 3: it takes `health` of rounds and flak (a rifle round's whole damage: two bring it down;
 * a mount's bursts as a light hull's) through a ball `hitPeople` across its middle. Proposals.
 */
export const SUPPLY_DRONE = { speedPeople: 15, heightPeople: 10, climbSeconds: 3, groundSeconds: 3, reloadSeconds: 30, stock: 2,
  /** step 2: a called ammunition load, one typed crate in the cage (69.34's crates: 2 rockets, 3 mortar bombs; a missile's case doesn't fit) */
  ammo: { rocket: 2, mortarBomb: 3 },
  /** step 3: the drone as a target */
  health: 40, hitPeople: 0.9 };
/**
 * DECISIONS 402 (397; GameConfig.finiteSupply, core/supplyStock.ts): each side's magazines and bandages a finite stock in its
 * motor pool's containers. DECISIONS 408 (the owner: "scaling starting supply to troop count probably makes sense in this
 * 'quickmatch' setup just so we know where the line is"; his pick on the mega's A/B, 0.75): `share` of a life's issue (its
 * guns' magazines and spares, 406, and BANDAGE.carried) for every life the side has — its units and its tickets. A side
 * with no ticket count (unlimited arrivals) gets `refills` issues a unit, 402's first sizing. A container takes
 * `containerHealth` before it is lost with what it holds; the base dumps are topped up from the stock every `restockS`
 * seconds. Proposals, to tune in play.
 */
export const SUPPLY_STOCK = {
  share: 0.75, refills: 4.2, containerHealth: 300, restockS: 1,
  /**
   * DECISIONS 406 (the owner: "APCs and M113s should carry some bandages and basic restock for small arms, so bots can re-arm
   * remotely"): the load an APC (`wheeled`) or M113 (`tracked`) carries — bandages, bullet magazines and explosive small arms
   * by kind — drawn from its side's stock within `reachPeople` of its base dump, and refilled there. Proposals.
   */
  carrier: { kinds: ['wheeled', 'tracked'] as const, bandages: 4, magazines: 8, explosives: { he: 2, smoke: 1, gl40: 4, rocket: 1 }, reachPeople: 15 },
};
/** A container's pallet spots: across × along × tiers (32). */
export const CONTAINER_SPOTS = { across: 2, along: 8, tiers: 2 };
/** How many pallets a supply truck's bed takes (1.72 × 2.4 m: two across, two along). */
export const TRUCK_PALLETS = 4;

/** Every pallet: what it holds (today's units) and the spots it takes in a container. */
export const PALLETS: Readonly<Record<PalletKind, { contents: SupplyContents; spots: number; label: string }>> = {
  supply: { contents: { bandages: SUPPLY.bandages, magazines: SUPPLY.magazines }, spots: 1, label: 'stores: a supply dump\'s stock' },
  medical: { contents: { bandages: 2 * SUPPLY.bandages }, spots: 1, label: 'medical: bandages' },
  smallArms: { contents: { magazines: 2 * SUPPLY.magazines }, spots: 1, label: 'small arms: magazines in cans' },
  shells: { contents: { shells: GUN_STOCK.crate }, spots: 1, label: 'gun shells: a dump\'s crate' },
  ordnance: { contents: { grenades: 12, rockets: 6, gl40: 12 }, spots: 1, label: 'ordnance: grenades, LAW tubes, 40 mm' },
  material: { contents: { material: CONVOY.material }, spots: 1, label: 'material: a truck load\'s (a FOB\'s cost)' },
  fobKit: { contents: { fobKits: 1 }, spots: 4, label: 'a FOB\'s kit: Hesco flat-packs, sandbags' },
  hmgKit: { contents: { hmgKits: 1 }, spots: 1, label: 'an HMG\'s kit: the gun, its tripod, belts' },
  relays: { contents: { relays: 4 }, spots: 1, label: 'relays: four masts in cases' },
  beacons: { contents: { beacons: 4 }, spots: 1, label: 'beacons: four in cases' },
  drones: { contents: { drones: 2 * DRONE_PAYLOADS.crateDrones, charges: 2 * DRONE_PAYLOADS.crateCharges }, spots: 1, label: 'drones and charges' },
};

/**
 * Each packing's pallets (in loading order, front to back): the starting pool's containers (core/motorPool.ts
 * MOTOR_POOL_SUPPLIES). `stores` the base's convoy stock (CONVOY.baseStock truck loads) and its dumps' and FOBs' crates; `kits`
 * the side's build kits (BUILD_KITS) and a reserve of material; `ammo` the guns' shells, the side's ordnance and small arms;
 * `medical` bandages; `fuel` its bladder (no pallets: nothing in play burns fuel yet).
 */
export const CONTAINER_PACKINGS: Readonly<Record<ContainerPacking, ReadonlyArray<{ pallet: PalletKind; count: number }>>> = {
  stores: [{ pallet: 'supply', count: 2 * CONVOY.baseStock + 2 + 4 }, { pallet: 'material', count: CONVOY.baseStock }],
  kits: [{ pallet: 'fobKit', count: BUILD_KITS.fob }, { pallet: 'hmgKit', count: BUILD_KITS.hmg }, { pallet: 'relays', count: BUILD_KITS.relay / 4 }, { pallet: 'beacons', count: BUILD_KITS.beacon / 4 }, { pallet: 'drones', count: 2 }, { pallet: 'material', count: 6 }],
  ammo: [{ pallet: 'shells', count: 6 }, { pallet: 'ordnance', count: 6 }, { pallet: 'smallArms', count: 12 }],
  medical: [{ pallet: 'medical', count: 12 }],
  fuel: [],
};

/** What a packing holds altogether (today's units) and the spots it fills. */
export function packingTotals(packing: ContainerPacking): { contents: SupplyContents; spots: number; pallets: number } {
  const contents: SupplyContents = {};
  let spots = 0, pallets = 0;
  for (const { pallet, count } of CONTAINER_PACKINGS[packing]) {
    const p = PALLETS[pallet];
    spots += p.spots * count; pallets += count;
    for (const [k, v] of Object.entries(p.contents) as [keyof SupplyContents, number][]) contents[k] = (contents[k] ?? 0) + v * count;
  }
  return { contents, spots, pallets };
}
